// src/edu-world/systems/minigame-launcher.ts
// Minigame_Launcher: narrative transition into/out of minigame (Phase 1)
// Fades world out, shows counting minigame scene, fades back on completion.

import Phaser from 'phaser';
import type { MinigameInput, MinigameOutput } from '../types';
import type { EventBus } from '../core/event-bus';

export class MinigameLauncher {
  private scene: Phaser.Scene;
  private eventBus: EventBus;
  private isActive: boolean = false;
  private savedPlayerPosition: { x: number; y: number } = { x: 0, y: 0 };

  constructor(scene: Phaser.Scene, eventBus: EventBus) {
    this.scene = scene;
    this.eventBus = eventBus;
  }

  /** Launch minigame with entry transition */
  launch(input: MinigameInput, playerPosition: { x: number; y: number }): void {
    if (this.isActive) return;
    this.isActive = true;
    this.savedPlayerPosition = { ...playerPosition };

    // ── Entry transition: fade camera out (1 second) ──
    this.scene.cameras.main.fadeOut(800, 76, 175, 80); // green tint fade

    this.scene.cameras.main.once('camerafadeoutcomplete', () => {
      // Pause world scenes while minigame is active
      this.scene.scene.pause('WorldEngine');
      this.scene.scene.pause('EduWorldOrchestrator');

      // Start the minigame scene (it renders independently)
      this.scene.scene.launch('CountingMinigame', {
        input,
        eventBus: this.eventBus,
        onComplete: (output: MinigameOutput) => this.handleComplete(output),
      });
    });
  }

  /** Handle minigame completion and exit transition */
  private handleComplete(output: MinigameOutput): void {
    // Stop the minigame scene
    this.scene.scene.stop('CountingMinigame');

    // Resume world scenes
    this.scene.scene.resume('WorldEngine');
    this.scene.scene.resume('EduWorldOrchestrator');

    // ── Exit transition: fade camera back in ──
    this.scene.cameras.main.fadeIn(800, 76, 175, 80);

    this.scene.cameras.main.once('camerafadeincomplete', () => {
      this.isActive = false;

      // Emit completion event
      this.eventBus.emit('minigame:complete', {
        mission_id: output.mission_id,
        success: output.success,
        score: output.score,
        attempts: output.attempts,
        duration_seconds: output.duration_seconds,
      });
    });
  }

  getIsActive(): boolean {
    return this.isActive;
  }

  getSavedPlayerPosition(): { x: number; y: number } {
    return this.savedPlayerPosition;
  }
}
